import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useSearchParams } from "react-router-dom";
import { useSelector } from "react-redux";
import {
  ChartBarIcon,
  ChevronDownIcon,
  FunnelIcon,
  GlobeAltIcon,
  SignalIcon,
} from "@heroicons/react/24/outline";
import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Legend,
  Line,
  LineChart,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

import FeatureGate from "../../components/account/FeatureGate";
import DashboardLayout from "../../components/DashboardLayout";
import WeaveLoader from "../../components/common/WeaveLoader";
import { projectsService } from "../../services/projects";
import { sitePath } from "../../utils/urls";

const SECTIONS = [
  { id: "traffic", label: "Traffic" },
  { id: "performance", label: "Performance" },
  { id: "infrastructure", label: "Infrastructure" },
];
const RANGES = [
  { days: 1, label: "24h" },
  { days: 7, label: "7d" },
  { days: 30, label: "30d" },
  { days: 90, label: "90d" },
];
const FILTER_KEYS = ["path", "source", "country", "device", "browser", "os"];
const CHART_TOOLTIP = {
  backgroundColor: "var(--theme-main-bg)",
  border: "1px solid var(--theme-shadow)",
  borderRadius: "10px",
  color: "var(--theme-text)",
  fontSize: "12px",
};

const Analytics = ({ currentUser }) => {
  const project = useSelector((state) => state.project.currentProject);
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);
  const [filterOptions, setFilterOptions] = useState({});
  const hasAnalyticsAccess = Boolean(currentUser?.features?.analytics);
  const section = SECTIONS.find(
    (item) => location.pathname.includes(`/analytics/${item.id}/`),
  )?.id || "traffic";
  const requestedDays = Number(searchParams.get("days")) || 7;
  const days = RANGES.some((range) => range.days === requestedDays) ? requestedDays : 7;
  const filters = useMemo(() => {
    const params = new URLSearchParams(location.search);
    return Object.fromEntries(
      FILTER_KEYS
        .map((key) => [key, params.get(key)])
        .filter(([, value]) => value !== null),
    );
  }, [location.search]);

  const loadAnalytics = useCallback(() => {
    if (!project || !hasAnalyticsAccess) return;
    setLoading(true);
    setError("");
    projectsService.getProjectAnalytics(
      project.hash,
      {
        section,
        days: section === "infrastructure" ? 7 : days,
        ...filters,
      },
      (response) => {
        setData(response.data);
        if (section === "traffic" && Object.keys(filters).length === 0) {
          setFilterOptions({
            path: (response.data.pages || []).map((item) => ({
              value: item.path,
              label: item.path,
            })),
            source: (response.data.sources || []).map((item) => ({
              value: item.value,
              label: item.name,
            })),
            country: (response.data.countries || []).map((item) => ({
              value: item.name,
              label: item.name,
            })),
            device: (response.data.devices || []).map((item) => ({
              value: item.name,
              label: item.name,
            })),
            browser: (response.data.browsers || []).map((item) => ({
              value: item.name,
              label: item.name,
            })),
            os: (response.data.operating_systems || []).map((item) => ({
              value: item.name,
              label: item.name,
            })),
          });
        }
        setLoading(false);
      },
      (err) => {
        setError(err?.response?.data?.error || "Could not load analytics.");
        setLoading(false);
      },
    );
  }, [project, section, days, filters, hasAnalyticsAccess]);

  useEffect(loadAnalytics, [loadAnalytics]);

  if (!hasAnalyticsAccess) {
    return (
      <DashboardLayout title="Analytics" currentUser={currentUser} navConfig={null}>
        <FeatureGate
          icon={<ChartBarIcon className="h-16 w-16 text-theme-primary" />}
          title="Site analytics"
          description="Track traffic, performance, and delivery for your hosted site."
          featureLabel="Analytics"
          bullets={[
            { icon: GlobeAltIcon, text: "Visitor traffic and sources" },
            { icon: SignalIcon, text: "Real-user performance signals" },
            { icon: ChartBarIcon, text: "Requests, caching, and delivery" },
          ]}
        />
      </DashboardLayout>
    );
  }

  const setFilter = (key, value) => {
    const next = new URLSearchParams(searchParams);
    if (value) next.set(key, value);
    else next.delete(key);
    setSearchParams(next, { replace: true });
  };

  const clearFilters = () => {
    const next = new URLSearchParams(searchParams);
    FILTER_KEYS.forEach((key) => next.delete(key));
    setSearchParams(next, { replace: true });
  };

  const setDays = (value) => {
    const next = new URLSearchParams(searchParams);
    next.set("days", value);
    setSearchParams(next, { replace: true });
  };

  const query = searchParams.toString();
  const navConfig = SECTIONS.map((item) => ({
    ...item,
    to: `${sitePath(project, `analytics/${item.id}/`)}${query ? `?${query}` : ""}`,
    active: section === item.id,
  }));
  let content;
  if (loading && !data) {
    content = <LoadingState />;
  } else if (error) {
    content = <ErrorState error={error} onRetry={loadAnalytics} />;
  } else if (!data?.configured) {
    content = (
      <EmptyState
        title="Analytics not configured"
        message={
          data?.hostname
            ? `Cloudflare analytics are not available for ${data.hostname}.`
            : "Deploy your site to a live hostname before analytics are available."
        }
      />
    );
  } else {
    content = (
      <div className={loading ? "opacity-60 transition-opacity" : ""}>
        {section === "traffic" && (
          <TrafficTab data={data} onFilter={setFilter} days={days} />
        )}
        {section === "performance" && <PerformanceTab data={data} days={days} />}
        {section === "infrastructure" && <InfrastructureTab data={data} />}
      </div>
    );
  }

  return (
    <DashboardLayout
      title="Analytics"
      currentUser={currentUser}
      navConfig={navConfig}
    >
      <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
        <div>
          <p className="text-sm font-semibold">
            {section === "traffic" && "Visitor traffic and acquisition"}
            {section === "performance" && "Real-user page performance"}
            {section === "infrastructure" && "Requests, caching, and delivery"}
          </p>
          <p className="mt-1 text-xs opacity-50">
            {section === "infrastructure"
              ? "Cloudflare HTTP data, last 7 days"
              : `Cloudflare Web Analytics, last ${days === 1 ? "24 hours" : `${days} days`}`}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {section === "traffic" && (
            <>
              {Object.keys(filters).length > 0 && (
                <button
                  type="button"
                  onClick={clearFilters}
                  className="px-1 text-xs font-semibold text-theme-primary hover:opacity-80"
                >
                  Clear Filters
                </button>
              )}
              <FilterPopover
                filters={filters}
                options={filterOptions}
                onChange={setFilter}
                onClear={clearFilters}
              />
            </>
          )}
          {section === "infrastructure" ? (
            <span className="rounded-lg border border-theme-secondary px-3 py-2 text-xs font-semibold opacity-60">
              Last 7 days
            </span>
          ) : (
            <div className="flex rounded-lg border border-theme-secondary p-1">
              {RANGES.map((range) => (
                <button
                  key={range.days}
                  type="button"
                  onClick={() => setDays(range.days)}
                  className={`rounded-md px-3 py-1.5 text-xs font-semibold transition ${
                    days === range.days
                      ? "bg-theme-secondary text-theme-primary"
                      : "opacity-50 hover:opacity-80"
                  }`}
                >
                  {range.label}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      {section !== "traffic" && Object.keys(filters).length > 0 && (
        <ActiveFilters filters={filters} onChange={setFilter} onClear={clearFilters} />
      )}

      {content}
    </DashboardLayout>
  );
};

const TrafficTab = ({ data, onFilter, days }) => {
  const topSource = data.sources?.[0];
  const topPage = data.pages?.[0];

  return (
    <>
      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-5">
        <StatCard
          label="Visits"
          value={formatNumber(data.totals?.visits)}
          delta={formatDelta(data.totals?.visits, data.previous?.visits)}
        />
        <StatCard
          label="Pageviews"
          value={formatNumber(data.totals?.pageviews)}
          delta={formatDelta(data.totals?.pageviews, data.previous?.pageviews)}
        />
        <StatCard
          label="Pages per visit"
          value={formatDecimal(data.totals?.pages_per_visit)}
          delta={formatDelta(
            data.totals?.pages_per_visit,
            data.previous?.pages_per_visit,
          )}
        />
        <StatCard label="Top source" value={topSource?.name || "No data"} compact />
        <StatCard label="Top page" value={topPage?.path || "No data"} compact />
      </div>

      <ChartCard title="Traffic over time" className="mt-6">
        {data.timeseries?.length ? (
          <div className="h-72" role="img" aria-label="Visits and pageviews over time">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={data.timeseries} margin={{ top: 8, right: 8, left: -20, bottom: 0 }}>
                <defs>
                  <linearGradient id="pageviewFill" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="var(--theme-primary)" stopOpacity={0.35} />
                    <stop offset="95%" stopColor="var(--theme-primary)" stopOpacity={0.02} />
                  </linearGradient>
                </defs>
                <CartesianGrid stroke="var(--theme-shadow)" strokeDasharray="3 3" vertical={false} />
                <XAxis
                  dataKey="time"
                  tickFormatter={(value) => formatChartTime(value, days)}
                  stroke="var(--theme-secondary-text)"
                  tickLine={false}
                  axisLine={false}
                  fontSize={11}
                  minTickGap={28}
                />
                <YAxis
                  stroke="var(--theme-secondary-text)"
                  tickLine={false}
                  axisLine={false}
                  fontSize={11}
                  allowDecimals={false}
                />
                <Tooltip
                  contentStyle={CHART_TOOLTIP}
                  labelFormatter={(value) => formatChartTime(value, days, true)}
                  formatter={(value, name) => [formatNumber(value), capitalize(name)]}
                />
                <Legend wrapperStyle={{ fontSize: "12px" }} />
                <Area
                  type="monotone"
                  dataKey="pageviews"
                  stroke="var(--theme-primary)"
                  fill="url(#pageviewFill)"
                  strokeWidth={2}
                />
                <Area
                  type="monotone"
                  dataKey="visits"
                  stroke="var(--theme-secondary)"
                  fill="transparent"
                  strokeWidth={2}
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        ) : (
          <EmptyChart />
        )}
      </ChartCard>

      <div className="mt-6 grid gap-6 xl:grid-cols-2">
        <TopTable
          title="Top sources"
          rows={data.sources || []}
          labelKey="name"
          valueKey="visits"
          valueLabel="visits"
          total={data.totals?.visits}
          onSelect={(row) => onFilter("source", row.value)}
        />
        <TopTable
          title="Top pages"
          rows={data.pages || []}
          labelKey="path"
          valueKey="pageviews"
          valueLabel="views"
          total={data.totals?.pageviews}
          onSelect={(row) => onFilter("path", row.path)}
        />
      </div>

      <div className="mt-6 grid gap-6 lg:grid-cols-3">
        <AudienceCard
          title="Countries"
          rows={data.countries || []}
          onSelect={(row) => onFilter("country", row.name)}
        />
        <DeviceCard
          rows={data.devices || []}
          onSelect={(row) => onFilter("device", row.name)}
        />
        <AudienceCard
          title="Browsers"
          rows={data.browsers || []}
          onSelect={(row) => onFilter("browser", row.name)}
        />
        <AudienceCard
          title="Operating systems"
          rows={data.operating_systems || []}
          onSelect={(row) => onFilter("os", row.name)}
        />
        <AudienceCard title="Navigation" rows={data.navigation || []} />
      </div>
    </>
  );
};

const PerformanceTab = ({ data, days }) => (
  <>
    <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-6">
      <MetricCard label="Page load" value={formatDuration(data.vitals?.page_load)} />
      <MetricCard label="LCP" value={formatDuration(data.vitals?.lcp)} hint={metricRating("lcp", data.vitals?.lcp)} />
      <MetricCard label="INP" value={formatDuration(data.vitals?.inp)} hint={metricRating("inp", data.vitals?.inp)} />
      <MetricCard label="CLS" value={formatCls(data.vitals?.cls)} hint={metricRating("cls", data.vitals?.cls)} />
      <MetricCard label="TTFB" value={formatDuration(data.vitals?.ttfb)} />
      <MetricCard label="FCP" value={formatDuration(data.vitals?.fcp)} />
    </div>

    <ChartCard title="Performance over time" className="mt-6">
      {data.timeseries?.length ? (
        <div className="h-72" role="img" aria-label="Page performance over time">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={data.timeseries} margin={{ top: 8, right: 8, left: -10, bottom: 0 }}>
              <CartesianGrid stroke="var(--theme-shadow)" strokeDasharray="3 3" vertical={false} />
              <XAxis
                dataKey="time"
                tickFormatter={(value) => formatChartTime(value, days)}
                stroke="var(--theme-secondary-text)"
                tickLine={false}
                axisLine={false}
                fontSize={11}
                minTickGap={28}
              />
              <YAxis
                stroke="var(--theme-secondary-text)"
                tickLine={false}
                axisLine={false}
                fontSize={11}
                tickFormatter={(value) => `${Math.round(value)} ms`}
              />
              <Tooltip
                contentStyle={CHART_TOOLTIP}
                labelFormatter={(value) => formatChartTime(value, days, true)}
                formatter={(value, name) => [formatDuration(value), metricLabel(name)]}
              />
              <Legend formatter={metricLabel} wrapperStyle={{ fontSize: "12px" }} />
              <Line type="monotone" dataKey="page_load" stroke="var(--theme-text)" strokeOpacity={0.45} dot={false} strokeWidth={2} />
              <Line type="monotone" dataKey="lcp" stroke="var(--theme-primary)" dot={false} strokeWidth={2} />
              <Line type="monotone" dataKey="ttfb" stroke="var(--theme-secondary)" dot={false} strokeWidth={2} />
            </LineChart>
          </ResponsiveContainer>
        </div>
      ) : (
        <EmptyChart />
      )}
    </ChartCard>

    <div className="mt-6 grid gap-6 lg:grid-cols-3">
      {["lcp", "inp", "cls"].map((metric) => (
        <RatingCard key={metric} metric={metric} values={data.ratings?.[metric] || {}} />
      ))}
    </div>

    <div className="mt-6 rounded-xl border border-theme-secondary bg-theme-main p-5">
      <h2 className="text-sm font-semibold">Performance by page</h2>
      {data.pages?.length ? (
        <div className="mt-4 overflow-x-auto">
          <table className="w-full min-w-[560px] text-left text-sm">
            <thead className="text-xs opacity-50">
              <tr>
                <th className="pb-3 font-semibold">Page</th>
                <th className="pb-3 text-right font-semibold">LCP</th>
                <th className="pb-3 text-right font-semibold">INP</th>
                <th className="pb-3 text-right font-semibold">CLS</th>
                <th className="pb-3 text-right font-semibold">Samples</th>
              </tr>
            </thead>
            <tbody>
              {data.pages.slice(0, 12).map((page) => (
                <tr key={page.path} className="border-t border-theme-secondary">
                  <td className="max-w-[20rem] truncate py-3 font-medium">{page.path}</td>
                  <td className="py-3 text-right">{formatDuration(page.lcp)}</td>
                  <td className="py-3 text-right">{formatDuration(page.inp)}</td>
                  <td className="py-3 text-right">{formatCls(page.cls)}</td>
                  <td className="py-3 text-right opacity-60">{formatNumber(page.measurements)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <EmptyChart />
      )}
    </div>
  </>
);

const InfrastructureTab = ({ data }) => {
  const cache = data.cache || [];
  const cacheRequests = cache.reduce((sum, item) => sum + item.requests, 0);
  const cacheHits = cache
    .filter((item) => ["hit", "stale", "updating", "revalidated"].includes(String(item.status).toLowerCase()))
    .reduce((sum, item) => sum + item.requests, 0);
  const errors = (data.statuses || [])
    .filter((item) => Number(item.status) >= 400)
    .reduce((sum, item) => sum + item.requests, 0);

  return (
    <>
      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <StatCard label="Requests" value={formatNumber(data.totals?.requests)} />
        <StatCard label="Data transfer" value={formatBytes(data.totals?.bytes)} />
        <StatCard
          label="Cache hit rate"
          value={cacheRequests ? `${Math.round((cacheHits / cacheRequests) * 100)}%` : "0%"}
        />
        <StatCard label="Errors" value={formatNumber(errors)} />
      </div>

      <ChartCard title="Requests over time" className="mt-6">
        {data.daily?.length ? (
          <div className="h-64" role="img" aria-label="HTTP requests over time">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={data.daily} margin={{ top: 8, right: 8, left: -20, bottom: 0 }}>
                <CartesianGrid stroke="var(--theme-shadow)" strokeDasharray="3 3" vertical={false} />
                <XAxis dataKey="date" tickFormatter={(value) => value.slice(5)} stroke="var(--theme-secondary-text)" tickLine={false} axisLine={false} fontSize={11} />
                <YAxis stroke="var(--theme-secondary-text)" tickLine={false} axisLine={false} fontSize={11} allowDecimals={false} />
                <Tooltip contentStyle={CHART_TOOLTIP} formatter={(value) => [formatNumber(value), "Requests"]} />
                <Bar dataKey="requests" fill="var(--theme-primary)" radius={[5, 5, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        ) : (
          <EmptyChart />
        )}
      </ChartCard>

      <div className="mt-6 grid gap-6 lg:grid-cols-3">
        <SimpleBarCard title="Cache status" rows={data.cache || []} labelKey="status" valueKey="requests" />
        <SimpleBarCard title="Response status" rows={data.statuses || []} labelKey="status" valueKey="requests" />
        <SimpleBarCard title="Top countries" rows={data.countries || []} labelKey="country" valueKey="requests" />
      </div>

      <div className="mt-6">
        <TopTable
          title="Top requested pages"
          rows={data.top_pages || []}
          labelKey="path"
          valueKey="requests"
          valueLabel="requests"
          total={data.totals?.requests}
        />
      </div>
    </>
  );
};

const FilterPopover = ({ filters, options, onChange, onClear }) => {
  const [open, setOpen] = useState(false);
  const rootRef = useRef(null);
  const activeCount = Object.keys(filters).length;

  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  return (
    <div className="relative" ref={rootRef}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className={`inline-flex items-center gap-1.5 rounded-lg border border-theme-secondary px-3 py-2 text-xs font-semibold transition ${
          open || activeCount > 0
            ? "bg-theme-secondary text-theme-primary"
            : "opacity-60 hover:opacity-100"
        }`}
      >
        <FunnelIcon className="h-3.5 w-3.5" />
        Filters
        {activeCount > 0 ? (
          <span>({activeCount})</span>
        ) : (
          <ChevronDownIcon className={`h-3.5 w-3.5 transition ${open ? "rotate-180" : ""}`} />
        )}
      </button>
      {open && (
        <div className="absolute right-0 z-30 mt-1.5 w-64 rounded-xl border border-theme-secondary bg-theme-main p-3 shadow-xl">
          <div className="space-y-3">
            <FilterSelect label="Page" filterKey="path" value={filters.path} options={options.path} onChange={onChange} />
            <FilterSelect label="Source" filterKey="source" value={filters.source} options={options.source} onChange={onChange} />
            <FilterSelect label="Country" filterKey="country" value={filters.country} options={options.country} onChange={onChange} />
            <FilterSelect label="Device" filterKey="device" value={filters.device} options={options.device} onChange={onChange} />
            <FilterSelect label="Browser" filterKey="browser" value={filters.browser} options={options.browser} onChange={onChange} />
            <FilterSelect label="OS" filterKey="os" value={filters.os} options={options.os} onChange={onChange} />
          </div>
          {activeCount > 0 && (
            <button
              type="button"
              onClick={onClear}
              className="mt-3 w-full px-2 py-1.5 text-left text-xs font-semibold text-theme-primary"
            >
              Clear filters
            </button>
          )}
        </div>
      )}
    </div>
  );
};

const FilterSelect = ({ label, filterKey, value = "", options = [], onChange }) => {
  const hasSelectedOption = options.some((option) => option.value === value);
  return (
    <label className="block">
      <span className="mb-1.5 block text-[11px] font-semibold uppercase tracking-wide opacity-50">{label}</span>
      <select
        value={value}
        onChange={(event) => onChange(filterKey, event.target.value)}
        className="w-full rounded-lg border border-theme-secondary bg-theme-main px-3 py-2 text-xs text-theme-text focus:border-theme-primary focus:outline-none"
      >
        <option value="">All</option>
        {value && !hasSelectedOption && <option value={value}>{formatFilterValue(value)}</option>}
        {options.map((option) => (
          <option key={option.value} value={option.value}>{option.label}</option>
        ))}
      </select>
    </label>
  );
};

const ActiveFilters = ({ filters, onChange, onClear }) => (
  <div className="mb-6 flex flex-wrap items-center gap-2">
    {Object.entries(filters).map(([key, value]) => (
      <button
        key={key}
        type="button"
        onClick={() => onChange(key, "")}
        className="rounded-full border border-theme-secondary px-3 py-1.5 text-xs"
      >
        <span className="opacity-50">{capitalize(key)}:</span> {formatFilterValue(value)} ×
      </button>
    ))}
    <button type="button" onClick={onClear} className="px-2 py-1.5 text-xs font-semibold text-theme-primary">
      Clear all
    </button>
  </div>
);

const StatCard = ({ label, value, delta, compact = false }) => (
  <div className="min-w-0 rounded-xl border border-theme-secondary bg-theme-main p-4">
    <p className="text-[11px] font-semibold uppercase tracking-wide opacity-50">{label}</p>
    <p className={`mt-2 truncate font-bold ${compact ? "text-lg" : "text-2xl"}`} title={String(value)}>
      {value}
    </p>
    {delta && <p className="mt-1 text-xs text-theme-primary">{delta} vs previous period</p>}
  </div>
);

const MetricCard = ({ label, value, hint }) => (
  <div className="rounded-xl border border-theme-secondary bg-theme-main p-4">
    <p className="text-[11px] font-semibold uppercase tracking-wide opacity-50">{label}</p>
    <p className="mt-2 text-xl font-bold">{value}</p>
    {hint && <p className="mt-1 text-xs opacity-55">{hint}</p>}
  </div>
);

const ChartCard = ({ title, className = "", children }) => (
  <div className={`rounded-xl border border-theme-secondary bg-theme-main p-5 ${className}`}>
    <h2 className="mb-4 text-sm font-semibold">{title}</h2>
    {children}
  </div>
);

const TopTable = ({ title, rows, labelKey, valueKey, valueLabel, total, onSelect }) => (
  <div className="rounded-xl border border-theme-secondary bg-theme-main p-5">
    <h2 className="text-sm font-semibold">{title}</h2>
    {rows.length ? (
      <div className="mt-3">
        {rows.slice(0, 10).map((row) => {
          const label = row[labelKey];
          const content = (
            <>
              <span className="min-w-0 flex-1 truncate text-left font-medium" title={String(label)}>{label}</span>
              <span className="shrink-0 text-xs opacity-55">
                {formatNumber(row[valueKey])} {valueLabel}
                {total ? ` · ${Math.round((row[valueKey] / total) * 100)}%` : ""}
              </span>
            </>
          );
          return onSelect ? (
            <button
              key={label}
              type="button"
              onClick={() => onSelect(row)}
              className="flex w-full items-center gap-3 border-t border-theme-secondary py-3 text-sm first:border-0 hover:text-theme-primary"
            >
              {content}
            </button>
          ) : (
            <div key={label} className="flex items-center gap-3 border-t border-theme-secondary py-3 text-sm first:border-0">
              {content}
            </div>
          );
        })}
      </div>
    ) : (
      <EmptyChart />
    )}
  </div>
);

const AudienceCard = ({ title, rows, onSelect }) => (
  <SimpleBarCard
    title={title}
    rows={rows}
    labelKey="name"
    valueKey="pageviews"
    onSelect={onSelect}
  />
);

const DeviceCard = ({ rows, onSelect }) => {
  const total = rows.reduce((sum, row) => sum + row.pageviews, 0);
  return (
    <div className="rounded-xl border border-theme-secondary bg-theme-main p-5">
      <h2 className="text-sm font-semibold">Devices</h2>
      {rows.length ? (
        <div className="mt-3 flex items-center gap-4">
          <div className="h-36 w-36 shrink-0">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie data={rows} dataKey="pageviews" nameKey="name" innerRadius={38} outerRadius={60} paddingAngle={2}>
                  {rows.map((row, index) => (
                    <Cell key={row.name} fill="var(--theme-primary)" fillOpacity={Math.max(0.3, 1 - index * 0.18)} />
                  ))}
                </Pie>
                <Tooltip contentStyle={CHART_TOOLTIP} formatter={(value) => [formatNumber(value), "Pageviews"]} />
              </PieChart>
            </ResponsiveContainer>
          </div>
          <div className="min-w-0 flex-1 space-y-2">
            {rows.slice(0, 5).map((row) => (
              <button key={row.name} type="button" onClick={() => onSelect?.(row)} className="flex w-full items-center justify-between gap-2 text-xs hover:text-theme-primary">
                <span className="truncate capitalize">{row.name}</span>
                <span className="shrink-0 opacity-55">{total ? Math.round((row.pageviews / total) * 100) : 0}%</span>
              </button>
            ))}
          </div>
        </div>
      ) : (
        <EmptyChart />
      )}
    </div>
  );
};

const SimpleBarCard = ({ title, rows, labelKey, valueKey, onSelect }) => {
  const max = Math.max(1, ...rows.map((row) => row[valueKey]));
  return (
    <div className="rounded-xl border border-theme-secondary bg-theme-main p-5">
      <h2 className="text-sm font-semibold">{title}</h2>
      {rows.length ? (
        <div className="mt-4 space-y-3">
          {rows.slice(0, 8).map((row) => {
            const content = (
              <>
                <div className="mb-1 flex items-center justify-between gap-3 text-xs">
                  <span className="truncate font-medium capitalize">{row[labelKey]}</span>
                  <span className="shrink-0 opacity-55">{formatNumber(row[valueKey])}</span>
                </div>
                <div className="h-1.5 overflow-hidden rounded-full bg-theme-secondary">
                  <div className="h-full rounded-full bg-theme-primary" style={{ width: `${(row[valueKey] / max) * 100}%` }} />
                </div>
              </>
            );
            return onSelect ? (
              <button key={row[labelKey]} type="button" onClick={() => onSelect(row)} className="block w-full text-left hover:text-theme-primary">
                {content}
              </button>
            ) : (
              <div key={row[labelKey]}>{content}</div>
            );
          })}
        </div>
      ) : (
        <EmptyChart />
      )}
    </div>
  );
};

const RatingCard = ({ metric, values }) => {
  const total = Object.values(values).reduce((sum, value) => sum + value, 0);
  const ratings = [
    { key: "good", label: "Good", opacity: 1 },
    { key: "needs_improvement", label: "Needs improvement", opacity: 0.6 },
    { key: "poor", label: "Poor", opacity: 0.3 },
  ];
  return (
    <div className="rounded-xl border border-theme-secondary bg-theme-main p-5">
      <h2 className="text-sm font-semibold">{metricLabel(metric)} ratings</h2>
      {total ? (
        <>
          <div className="mt-5 flex h-3 overflow-hidden rounded-full bg-theme-secondary">
            {ratings.map((rating) => (
              <div
                key={rating.key}
                className="h-full bg-theme-primary"
                style={{
                  width: `${(values[rating.key] / total) * 100}%`,
                  opacity: rating.opacity,
                }}
              />
            ))}
          </div>
          <div className="mt-4 space-y-2">
            {ratings.map((rating) => (
              <div key={rating.key} className="flex justify-between text-xs">
                <span className="opacity-60">{rating.label}</span>
                <span className="font-semibold">{Math.round((values[rating.key] / total) * 100)}%</span>
              </div>
            ))}
          </div>
        </>
      ) : (
        <EmptyChart />
      )}
    </div>
  );
};

const LoadingState = () => (
  <div className="flex min-h-[40vh] items-center justify-center">
    <WeaveLoader size={72} />
  </div>
);

const ErrorState = ({ error, onRetry }) => (
  <div className="max-w-lg rounded-xl border border-theme-secondary p-8">
    <h2 className="text-lg font-bold">Could not load analytics</h2>
    <p className="mt-2 text-sm leading-6 opacity-60">{error}</p>
    <button type="button" onClick={onRetry} className="mt-5 rounded-lg bg-theme-primary px-4 py-2 text-sm font-semibold text-theme-button">
      Try again
    </button>
  </div>
);

const EmptyState = ({ title, message }) => (
  <div className="max-w-lg rounded-xl border border-dashed border-theme-secondary p-8">
    <h2 className="text-lg font-bold">{title}</h2>
    <p className="mt-2 text-sm leading-6 opacity-60">{message}</p>
  </div>
);

const EmptyChart = () => (
  <p className="py-8 text-center text-sm opacity-50">No data recorded in this period.</p>
);

const formatNumber = (value) => (value ?? 0).toLocaleString();

const formatDecimal = (value) => Number(value || 0).toFixed(2);

const formatBytes = (bytes) => {
  if (!bytes) return "0 B";
  const units = ["B", "KB", "MB", "GB"];
  let size = bytes;
  let unit = 0;
  while (size >= 1024 && unit < units.length - 1) {
    size /= 1024;
    unit += 1;
  }
  return `${size.toFixed(size >= 10 || unit === 0 ? 0 : 1)} ${units[unit]}`;
};

const formatDelta = (current, previous) => {
  if (!previous) return current ? "New" : "";
  const delta = Math.round(((current - previous) / previous) * 100);
  return `${delta > 0 ? "+" : ""}${delta}%`;
};

const formatDuration = (value) => {
  if (value === null || value === undefined) return "No data";
  return value >= 1000 ? `${(value / 1000).toFixed(2)} s` : `${Math.round(value)} ms`;
};

const formatCls = (value) => (
  value === null || value === undefined ? "No data" : Number(value).toFixed(3)
);

const formatChartTime = (value, days, detailed = false) => {
  if (!value) return "";
  if (days > 2) {
    const [year, month, day] = value.slice(0, 10).split("-");
    return detailed ? `${month}/${day}/${year}` : `${month}/${day}`;
  }
  const date = new Date(value);
  return date.toLocaleString([], detailed
    ? { month: "short", day: "numeric", hour: "numeric" }
    : { hour: "numeric" });
};

const metricRating = (metric, value) => {
  if (value === null || value === undefined) return "";
  const thresholds = {
    lcp: [2500, 4000],
    inp: [200, 500],
    cls: [0.1, 0.25],
  }[metric];
  if (!thresholds) return "";
  if (value <= thresholds[0]) return "Good";
  if (value <= thresholds[1]) return "Needs improvement";
  return "Poor";
};

const metricLabel = (metric) => ({
  page_load: "Page load",
  lcp: "LCP",
  inp: "INP",
  cls: "CLS",
  ttfb: "TTFB",
}[metric] || capitalize(metric));

const formatFilterValue = (value) => value === "__direct__" ? "Direct" : value;

const capitalize = (value) => String(value || "")
  .replaceAll("_", " ")
  .replace(/\b\w/g, (letter) => letter.toUpperCase());

export default Analytics;
